import { useMutation, useQueryClient } from "@tanstack/react-query";
import { buildDeleteItem } from "@/lib/apiClient/api";
import { useNavigate } from "react-router-dom";
import useModal from "@/hooks/useModal";
import useUiDispatch from "@/hooks/useUiDispatch";
import { showToast } from "@/lib/ui/actions/actions";
import { buildQueryKeys } from "@/lib/filters/funcs";
import { useQueryCacheHelpers } from "@/hooks/rqhooks/useQueryCacheHelpers";

const useDeleteMutation = (
  queryKey,
  {
    modalKey,
    successMessage,
    apiFunction,
    onSettled,
    apiEndpoint,
    skipOptimistic = false
  }
) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const openModal = useModal();
  const cacheHelpers = useQueryCacheHelpers();
  const dispatch = useUiDispatch();
  const queryKeyToUse = Array.isArray(queryKey) ? queryKey : [queryKey];
  const defaultOnSettled = () =>
    queryClient.invalidateQueries({ queryKey: queryKeyToUse });
  const deleteMutation = useMutation({
    mutationFn: apiFunction || buildDeleteItem({ apiEndpoint, queryKey }),
    onMutate: ({ confirmId: id }) => {
      if (!skipOptimistic) {
        cacheHelpers.removeItem(queryKeyToUse, id);
      }
      openModal({ isOpen: false, entity: modalKey });
    },
    onSuccess: (_data, { isDeletedInSingleView, listUrl, confirmId: id }) => {
      if (isDeletedInSingleView) {
        const queryKeyDetail = buildQueryKeys(queryKeyToUse[0]).detail(id);
        // remove detail query, so it doesn't try to refetech or show it's cache on back button
        queryClient.removeQueries({
          queryKey: queryKeyDetail,
          exact: true
        });
        navigate(listUrl);
      }
      dispatch(
        showToast({
          msg: successMessage || "Item has been successfully deleted."
        })
      );
    },
    onSettled: onSettled || defaultOnSettled
  });
  return deleteMutation;
};

export default useDeleteMutation;
